/**
 * Event Detail Page
 *
 * Admin page showing full event details with roles, timeline, and assignments.
 *
 * Uses React Query with polling for real-time updates.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEvent, useEventDocuments, useInvalidateDocuments, useEventDetailModals } from '../../../hooks';
import { DataFreshness } from '../../../components/ui/DataFreshness';
import { deleteEvent, confirmAssignments, updateEvent, reorderEventRoles, deleteEventRole } from '../../../api/events';
import {
  deleteAssignment,
  updateAssignment,
  listPendingNotifications,
  notifyAllPending,
} from '../../../api/assignments';
import { getErrorMessage } from '../../../api/client';
import { useOrganization } from '../../../context/OrganizationContext';
import { AssignCrewModal, HoursEntryModal } from '../../../components/assignments';
import { CrewProfileLink } from '../../../components/crew';
import { AddRoleModal } from '../../../components/events';
import { ConfirmDialog } from '../../../components/ui';
import { TimelineList, TimelineEntryModal } from '../../../components/timeline';
import { DocumentList, DocumentUploadModal } from '../../../components/documents';
import { ComposeMessageModal } from '../../../components/messages';
import { EventAvailabilityModal } from '../../../components/availability';
import { EVENT_STATUS, ASSIGNMENT_STATUS, type AssignmentStatus } from '../../../config/statusStyles';
import { formatTimeWithZone, formatLongDate } from '../../../utils/dateFormatting';
import { formatRate, formatPay, formatHours } from '../../../utils/rateFormatting';
import { effectiveHours, effectivePay, scheduledHours } from '../../../utils/assignmentHours';
import { isPendingNotification } from '../../../utils/pendingNotification';
import { SCHEDULED_HOURS_TITLE } from '../../../components/reports/ScheduledHoursBadge';
import type { Assignment, RateType, EventStatus, EventRole } from '../../../types/models';

/** Format rate for display */
function formatRoleRate(cents: number | null | undefined, type: string | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  const dollars = (cents / 100).toFixed(2);
  return type === 'FLAT' ? `$${dollars} flat` : `$${dollars}/hr`;
}

/** Hours modal state */
interface HoursModalState {
  isOpen: boolean;
  assignmentId: string;
  crewName: string;
  roleName: string;
  effectiveRate: number | null;
  effectiveRateType: RateType | null;
  hoursWorked: string | number | null;
  checkInTime: string | null;
  checkOutTime: string | null;
}

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { paymentTrackingEnabled } = useOrganization();
  const {
    event,
    loading,
    error,
    isRefetching,
    dataUpdatedAt,
    refetch,
  } = useEvent(id);
  const { documents, loading: documentsLoading } = useEventDocuments(id);
  const invalidateDocuments = useInvalidateDocuments();

  // All modal and action states managed by custom hook
  const modals = useEventDetailModals();

  // Hours entry modal state
  const [hoursModal, setHoursModal] = useState<HoursModalState>({
    isOpen: false,
    assignmentId: '',
    crewName: '',
    roleName: '',
    effectiveRate: null,
    effectiveRateType: null,
    hoursWorked: null,
    checkInTime: null,
    checkOutTime: null,
  });

  const openHoursModal = (assignment: Assignment, roleName: string) => {
    const crewName = assignment.user
      ? `${assignment.user.firstName} ${assignment.user.lastName}`
      : 'Unknown';

    setHoursModal({
      isOpen: true,
      assignmentId: assignment.id,
      crewName,
      roleName,
      effectiveRate: assignment.effectiveRate ?? null,
      effectiveRateType: assignment.effectiveRateType ?? null,
      hoursWorked: assignment.hoursWorked ?? null,
      checkInTime: assignment.checkInTime ?? null,
      checkOutTime: assignment.checkOutTime ?? null,
    });
  };

  const closeHoursModal = () => {
    setHoursModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDelete = async () => {
    if (!id) return;
    modals.actions.setIsDeleting(true);
    try {
      await deleteEvent(id);
      navigate('/admin/events');
    } catch (err) {
      modals.error.set(getErrorMessage(err));
      modals.actions.setIsDeleting(false);
    }
  };

  const handleConfirmAssignments = async () => {
    if (!id) return;
    modals.actions.setIsConfirming(true);
    try {
      await confirmAssignments(id);
      refetch();
    } catch (err) {
      modals.error.set(getErrorMessage(err));
    } finally {
      modals.actions.setIsConfirming(false);
    }
  };

  // Local role ordering — keeps drag-and-drop snappy without waiting on refetch.
  // Synced from server when the *set* of role IDs changes (add/remove); preserves
  // optimistic order during pending reorder requests and between polls.
  const [localRoleIds, setLocalRoleIds] = useState<string[] | null>(null);
  const serverRoleIds = useMemo(
    () => (event?.roles ?? []).map((r) => r.id),
    [event?.roles]
  );
  useEffect(() => {
    setLocalRoleIds((prev) => {
      if (!prev) return serverRoleIds;
      const sortedPrev = [...prev].sort().join(',');
      const sortedNext = [...serverRoleIds].sort().join(',');
      // Only re-sync when the membership changed (role added/removed)
      return sortedPrev === sortedNext ? prev : serverRoleIds;
    });
  }, [serverRoleIds]);

  const orderedRoles = useMemo(() => {
    const roles = event?.roles ?? [];
    if (!localRoleIds) return roles;
    const byId = new Map(roles.map((r) => [r.id, r] as const));
    return localRoleIds
      .map((rid) => byId.get(rid))
      .filter((r): r is NonNullable<typeof r> => !!r);
  }, [event?.roles, localRoleIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Edit/delete role state
  const [editingRole, setEditingRole] = useState<EventRole | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<EventRole | null>(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  const handleDeleteRole = async () => {
    if (!id || !roleToDelete) return;
    setIsDeletingRole(true);
    modals.error.clear();
    try {
      await deleteEventRole(id, roleToDelete.id);
      setRoleToDelete(null);
      await refetch();
    } catch (err) {
      modals.error.set(getErrorMessage(err));
    } finally {
      setIsDeletingRole(false);
    }
  };

  const handleRoleDragEnd = async (e: DragEndEvent) => {
    if (!id || !e.over || e.active.id === e.over.id) return;
    const current = localRoleIds ?? serverRoleIds;
    const oldIndex = current.indexOf(String(e.active.id));
    const newIndex = current.indexOf(String(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(current, oldIndex, newIndex);
    setLocalRoleIds(next);
    try {
      await reorderEventRoles(id, next);
    } catch (err) {
      modals.error.set(getErrorMessage(err));
      // Restore from server on failure
      setLocalRoleIds(serverRoleIds);
      refetch();
    }
  };

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  // Pending terminal-status change awaiting user confirmation. CANCELLED and
  // COMPLETED are accidental-click-dangerous (an admin can silently cancel
  // a published event), so we route them through a ConfirmDialog.
  const [pendingStatus, setPendingStatus] = useState<EventStatus | null>(null);
  const TERMINAL_STATUSES: EventStatus[] = ['CANCELLED', 'COMPLETED'];

  const applyStatusChange = async (newStatus: EventStatus) => {
    if (!id || !event || newStatus === event.status) return;
    modals.error.clear();
    setIsUpdatingStatus(true);
    try {
      await updateEvent(id, { status: newStatus });
      await refetch();
    } catch (err) {
      modals.error.set(getErrorMessage(err));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusChange = (newStatus: EventStatus) => {
    if (!event || newStatus === event.status) return;
    if (TERMINAL_STATUSES.includes(newStatus)) {
      setPendingStatus(newStatus);
      return;
    }
    void applyStatusChange(newStatus);
  };

  // ---------------------------------------------------------------------------
  // Pending assignment notifications (event-scoped send)
  //
  // Sending only ENQUEUES — the worker marks assignments notified a few
  // seconds later, so a refetch right after queueing would still show them
  // pending and read as "the message never went". Queued ids are treated as
  // notified for display; a send that ultimately fails (after the worker's
  // retries) leaves notificationSentAt null and resurfaces on the next visit.
  // ---------------------------------------------------------------------------
  const [queuedNotificationIds, setQueuedNotificationIds] = useState<Set<string>>(new Set());
  const [notifyConfirmOpen, setNotifyConfirmOpen] = useState(false);
  const [isSendingNotifications, setIsSendingNotifications] = useState(false);
  // Pending assignments the affected crew have on OTHER events — the
  // consolidated email covers those too, so the confirm dialog says so.
  const [otherEventPendingCount, setOtherEventPendingCount] = useState<number | null>(null);
  const pendingBannerRef = useRef<HTMLDivElement | null>(null);
  const [bannerHighlight, setBannerHighlight] = useState(false);

  type RoleAssignment = NonNullable<EventRole['assignments']>[number] &
    Pick<Assignment, 'notificationSentAt'>;

  // "Pending" follows utils/pendingNotification.ts (the backend's rule): still
  // ASSIGNED, never notified, event not cancelled. A confirmed person is never
  // pending. activeAssignments (anyone not declined/cancelled) stays separate:
  // it decides whether the green "Crew Notified" tag is meaningful at all.
  const allAssignments = useMemo(
    () => ((event?.roles ?? []).flatMap((r) => r.assignments ?? []) as RoleAssignment[]),
    [event?.roles]
  );
  const activeAssignments = useMemo(
    () =>
      event?.status === 'CANCELLED'
        ? []
        : allAssignments.filter((a) => a.status !== 'DECLINED' && a.status !== 'CANCELLED'),
    [allAssignments, event?.status]
  );
  const pendingAssignments = useMemo(
    () =>
      allAssignments.filter(
        (a) => isPendingNotification(a, event) && !queuedNotificationIds.has(a.id)
      ),
    [allAssignments, event, queuedNotificationIds]
  );

  // DRAFT events notify via "Publish & Notify Crew"; terminal statuses have
  // nothing worth notifying. The send banner is a PUBLISHED-event affordance.
  const showPendingBanner = pendingAssignments.length > 0 && event?.status === 'PUBLISHED';

  const handlePendingTagClick = () => {
    if (showPendingBanner && pendingBannerRef.current) {
      pendingBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setBannerHighlight(true);
      window.setTimeout(() => setBannerHighlight(false), 1600);
    } else {
      navigate('/admin/messages');
    }
  };

  const openNotifyConfirm = () => {
    setNotifyConfirmOpen(true);
    setOtherEventPendingCount(null);
    const userIds = new Set(pendingAssignments.map((a) => a.user.id));
    listPendingNotifications()
      .then(({ groups }) => {
        const other = groups
          .filter((g) => userIds.has(g.user.id))
          .reduce((sum, g) => sum + g.assignments.filter((a) => a.event.id !== id).length, 0);
        setOtherEventPendingCount(other);
      })
      .catch(() => setOtherEventPendingCount(null));
  };

  const markPendingAsQueued = () => {
    setQueuedNotificationIds((prev) => {
      const next = new Set(prev);
      pendingAssignments.forEach((a) => next.add(a.id));
      return next;
    });
  };

  const handleSendNotifications = async () => {
    if (!id) return;
    setIsSendingNotifications(true);
    try {
      const result = await notifyAllPending(id);
      markPendingAsQueued();
      setNotifyConfirmOpen(false);
      toast.success(
        `Notifications on their way to ${result.usersQueued} crew member${
          result.usersQueued !== 1 ? 's' : ''
        }.`
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSendingNotifications(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    modals.actions.setRemovingAssignment(assignmentId);
    try {
      await deleteAssignment(assignmentId);
      refetch();
    } catch (err) {
      modals.error.set(getErrorMessage(err));
    } finally {
      modals.actions.setRemovingAssignment(null);
    }
  };

  const handleConfirmAssignment = async (assignmentId: string) => {
    modals.actions.setConfirmingAssignment(assignmentId);
    try {
      await updateAssignment(assignmentId, { status: 'CONFIRMED' });
      refetch();
    } catch (err) {
      modals.error.set(getErrorMessage(err));
    } finally {
      modals.actions.setConfirmingAssignment(null);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="space-y-4">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Event</h2>
        <p className="text-red-600 mb-4">{error}</p>
        <Link to="/admin/events" className="text-primary-600 hover:text-primary-500">
          Back to Events
        </Link>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Event Not Found</h2>
        <Link to="/admin/events" className="text-primary-600 hover:text-primary-500">
          Back to Events
        </Link>
      </div>
    );
  }

  const statusInfo = EVENT_STATUS[event.status];
  const canManageAssignments = event.status !== 'COMPLETED' && event.status !== 'CANCELLED';

  return (
    <div>
      {/* Back link */}
      <Link
        to="/admin/events"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Events
      </Link>

      {/* Action error banner */}
      {modals.error.message && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {modals.error.message}
          <button onClick={modals.error.clear} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-8">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 break-words">{event.name}</h1>
            <div className="relative">
              <select
                aria-label="Change event status"
                value={event.status}
                onChange={(e) => handleStatusChange(e.target.value as EventStatus)}
                disabled={isUpdatingStatus}
                className={`appearance-none bg-none pl-2.5 pr-7 py-0.5 border-0 text-xs font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-rack disabled:opacity-60 disabled:cursor-wait ${statusInfo.className}`}
                title="Change event status"
              >
                {(Object.keys(EVENT_STATUS) as EventStatus[]).map((s) => (
                  <option key={s} value={s}>{EVENT_STATUS[s].label}</option>
                ))}
              </select>
              <svg
                className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 opacity-70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {activeAssignments.length > 0 && pendingAssignments.length === 0 && (
              <span className="px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                Crew Notified
              </span>
            )}
            {pendingAssignments.length > 0 && (
              <button
                type="button"
                onClick={handlePendingTagClick}
                className="px-2.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-orange-400 underline decoration-orange-400 underline-offset-2"
                title={
                  showPendingBanner
                    ? 'Show who still needs their assignment notification'
                    : 'Review pending notifications on the Messages page'
                }
              >
                {pendingAssignments.length} Notification{pendingAssignments.length !== 1 ? 's' : ''} Pending
              </button>
            )}
          </div>
          <p className="text-gray-600">
            {event.venue}
            {event.network ? ` • ${event.network}` : ''}
          </p>
          <DataFreshness
            dataUpdatedAt={dataUpdatedAt}
            onRefresh={refetch}
            isRefreshing={isRefetching}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {event.status === 'DRAFT' && (
            <button
              onClick={handleConfirmAssignments}
              disabled={modals.actions.isConfirming}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
            >
              {modals.actions.isConfirming ? 'Publishing...' : 'Publish & Notify Crew'}
            </button>
          )}
          <button
            onClick={modals.availability.open}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            title="See who is available for this event"
          >
            <svg className="w-4 h-4 inline mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            See Availability
          </button>
          <button
            onClick={modals.message.open}
            disabled={!event.roles?.some(r => (r.assignments?.length || 0) > 0)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!event.roles?.some(r => (r.assignments?.length || 0) > 0) ? 'No crew assigned to message' : 'Message assigned crew'}
          >
            <svg className="w-4 h-4 inline mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Message Crew
          </button>
          <Link
            to={`/admin/events/${event.id}/edit`}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Edit
          </Link>
          <button
            onClick={modals.deleteConfirm.open}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Pending assignment notifications banner */}
      {showPendingBanner && (
        <div
          ref={pendingBannerRef}
          className={`mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg transition-shadow duration-300 ${
            bannerHighlight ? 'ring-2 ring-amber-400 shadow-md' : ''
          }`}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900">
                {pendingAssignments.length} crew member{pendingAssignments.length !== 1 ? 's' : ''} on
                this event {pendingAssignments.length !== 1 ? "haven't" : "hasn't"} been notified of
                their assignment yet
              </p>
              <p className="text-sm text-amber-800 mt-0.5">
                {pendingAssignments.map((a, i) => (
                  <span key={a.id}>
                    {i > 0 && ', '}
                    <CrewProfileLink
                      userId={a.user.id}
                      className="underline hover:text-amber-900"
                    >
                      {a.user.firstName} {a.user.lastName}
                    </CrewProfileLink>
                  </span>
                ))}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Link
                to="/admin/messages"
                className="text-sm text-amber-800 underline hover:text-amber-900 whitespace-nowrap"
                title="Review, dismiss, or cancel pending notifications"
              >
                Review in Messages
              </Link>
              <button
                type="button"
                onClick={openNotifyConfirm}
                disabled={isSendingNotifications}
                className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50 whitespace-nowrap"
              >
                Send notifications ({pendingAssignments.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="space-y-6">
        {/* Top row: Event Details and Quick Stats side by side on large screens */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic info card */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Details</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatLongDate(event.eventDate)}
                </dd>
              </div>
              {event.callTime && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Crew Call</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatTimeWithZone(event.callTime)}</dd>
                </div>
              )}
              {event.startTime && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Start Time</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatTimeWithZone(event.startTime)}</dd>
                </div>
              )}
              {event.endTime && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">End Time</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatTimeWithZone(event.endTime)}</dd>
                </div>
              )}
              {event.sport && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sport</dt>
                  <dd className="mt-1 text-sm text-gray-900">{event.sport}</dd>
                </div>
              )}
              {event.eventType && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Event Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{event.eventType}</dd>
                </div>
              )}
            </dl>

            {/* Additional info */}
            {(event.tocInfo || event.engineeringNotes || event.additionalDetails) && (
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                {event.tocInfo && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">TOC Information</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{event.tocInfo}</dd>
                  </div>
                )}
                {event.engineeringNotes && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Engineering Notes</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{event.engineeringNotes}</dd>
                  </div>
                )}
                {event.additionalDetails && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Additional Details</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{event.additionalDetails}</dd>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Stats - sidebar on large screens */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Total Roles</dt>
                <dd className="text-sm font-medium text-gray-900">{event.roles?.length || 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Assigned Crew</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {event.roles?.reduce((sum, role) => sum + (role.assignments?.length || 0), 0) || 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Positions Needed</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {event.roles?.reduce((sum, role) => sum + role.requiredCount, 0) || 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Availability Responses</dt>
                <dd className="text-sm font-medium text-gray-900">{event._count?.availability || 0}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Timeline - full width */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Timeline</h2>
            <button
              onClick={modals.timeline.openNew}
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              + Add Entry
            </button>
          </div>
          <TimelineList
            entries={event.timelineEntries || []}
            showAutoBadge
            editable
            onEdit={modals.timeline.openEdit}
          />
        </div>

        {/* Documents - full width */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
            <button
              onClick={modals.document.open}
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              + Upload
            </button>
          </div>
          <DocumentList
            documents={documents}
            canDelete={true}
            onDeleted={() => invalidateDocuments(id!)}
            loading={documentsLoading}
          />
        </div>

        {/* Roles & Assignments - full width */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Roles & Assignments</h2>
            <button
              onClick={modals.addRole.open}
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              + Add Role
            </button>
          </div>
          {orderedRoles.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-500 mb-2">Drag the handle to reorder roles by hierarchy.</p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRoleDragEnd}>
                <SortableContext items={orderedRoles.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-8 px-2 py-3"><span className="sr-only">Reorder</span></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filled</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Crew</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orderedRoles.map((role) => {
                        const assignedCount = role.assignments?.length || 0;
                        const isFull = assignedCount >= role.requiredCount;

                        return (
                          <SortableRoleRow key={role.id} id={role.id}>{(handleProps) => (
                          <>
                        {/* Drag handle */}
                        <td className="px-2 py-4 text-center align-middle w-8">
                          <button
                            type="button"
                            aria-label="Drag to reorder"
                            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-rack rounded touch-none"
                            {...handleProps.attributes}
                            {...handleProps.listeners}
                            ref={handleProps.setActivatorNodeRef}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <circle cx="7" cy="5" r="1.5" />
                              <circle cx="13" cy="5" r="1.5" />
                              <circle cx="7" cy="10" r="1.5" />
                              <circle cx="13" cy="10" r="1.5" />
                              <circle cx="7" cy="15" r="1.5" />
                              <circle cx="13" cy="15" r="1.5" />
                            </svg>
                          </button>
                        </td>

                        {/* Role */}
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{role.roleName}</div>
                          {role.description && (
                            <div className="text-xs text-gray-500 mt-0.5">{role.description}</div>
                          )}
                          {paymentTrackingEnabled && role.rateOverride && (
                            <div className="text-xs text-primary-600 mt-0.5 font-medium">
                              {formatRoleRate(role.rateOverride, role.rateOverrideType)}
                            </div>
                          )}
                        </td>

                        {/* Filled count */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${isFull ? 'text-green-600' : 'text-gray-900'}`}>
                            {assignedCount}/{role.requiredCount}
                          </span>
                        </td>

                        {/* Assigned Crew */}
                        <td className="px-4 py-4">
                          {role.assignments && role.assignments.length > 0 ? (
                            <div className="space-y-1.5">
                              {role.assignments.map((assignment) => {
                                const statusInfo = ASSIGNMENT_STATUS[assignment.status as AssignmentStatus] ||
                                  ASSIGNMENT_STATUS.ASSIGNED;

                                // Cast to get payment fields
                                const typedAssignment = assignment as Assignment;
                                // Entered hours, else the event's crew-call-to-end span; pay
                                // follows. Same rule as the reports.
                                const { hours, defaulted: hoursDefaulted } = effectiveHours(typedAssignment, event);
                                const pay = effectivePay(typedAssignment, hours);

                                return (
                                  <div
                                    key={assignment.id}
                                    className="flex items-center gap-2 flex-wrap"
                                  >
                                    <span className="text-sm text-gray-900">
                                      <CrewProfileLink userId={assignment.user.id}>
                                        {assignment.user.firstName} {assignment.user.lastName}
                                      </CrewProfileLink>
                                    </span>
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusInfo.className}`}>
                                      {statusInfo.label}
                                    </span>
                                    {isPendingNotification(typedAssignment, event) &&
                                      !queuedNotificationIds.has(assignment.id) && (
                                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                        Notification Pending
                                      </span>
                                    )}

                                    {/* Payment info (when payment tracking enabled) */}
                                    {paymentTrackingEnabled && (
                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                        {typedAssignment.effectiveRate && (
                                          <span title="Rate">
                                            {formatRate(typedAssignment.effectiveRate, typedAssignment.effectiveRateType)}
                                          </span>
                                        )}
                                        {hours !== null && (
                                          <>
                                            <span className="text-gray-300">|</span>
                                            <span title={hoursDefaulted ? SCHEDULED_HOURS_TITLE : 'Hours entered'}>
                                              {formatHours(hours)}
                                              {hoursDefaulted && <span className="text-gray-400"> (scheduled)</span>}
                                            </span>
                                          </>
                                        )}
                                        {pay !== null && (
                                          <>
                                            <span className="text-gray-300">|</span>
                                            <span className="font-medium text-green-600" title="Pay">
                                              {formatPay(pay)}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    )}

                                    {canManageAssignments && (
                                      <div className="flex items-center gap-1 ml-auto">
                                        {/* Edit hours button (when payment tracking enabled) */}
                                        {paymentTrackingEnabled && (
                                          <button
                                            onClick={() => openHoursModal(typedAssignment, role.roleName)}
                                            className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded"
                                            title="Record hours worked"
                                          >
                                            {typedAssignment.hoursWorked ? 'Edit Hours' : 'Add Hours'}
                                          </button>
                                        )}
                                        {assignment.status === 'ASSIGNED' && (
                                          <button
                                            onClick={() => handleConfirmAssignment(assignment.id)}
                                            disabled={modals.actions.confirmingAssignment === assignment.id}
                                            className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded disabled:opacity-50"
                                            title="Confirm assignment for crew member"
                                          >
                                            {modals.actions.confirmingAssignment === assignment.id ? '...' : 'Confirm'}
                                          </button>
                                        )}
                                        <button
                                          onClick={() => handleRemoveAssignment(assignment.id)}
                                          disabled={modals.actions.removingAssignment === assignment.id}
                                          className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                                          title="Remove assignment"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">No one assigned</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-3">
                            {canManageAssignments && !isFull && (
                              <button
                                onClick={() => modals.assignCrew.open(role.id, role.roleName)}
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                              >
                                + Assign
                              </button>
                            )}
                            <button
                              onClick={() => setEditingRole(role)}
                              className="text-gray-400 hover:text-gray-700"
                              title="Edit role"
                              aria-label={`Edit role ${role.roleName}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setRoleToDelete(role)}
                              className="text-gray-400 hover:text-red-600"
                              title="Delete role"
                              aria-label={`Delete role ${role.roleName}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                          </>
                          )}</SortableRoleRow>
                        );
                      })}
                    </tbody>
                  </table>
                </SortableContext>
              </DndContext>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No roles defined yet</p>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={modals.deleteConfirm.isOpen}
        onClose={modals.deleteConfirm.close}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${event.name}"? This action cannot be undone and will remove all associated roles, assignments, and timeline entries.`}
        confirmLabel="Delete"
        isLoading={modals.actions.isDeleting}
        variant="danger"
      />

      {/* Assign Crew Modal */}
      {modals.assignCrew.selectedRole && (
        <AssignCrewModal
          isOpen={modals.assignCrew.isOpen}
          onClose={modals.assignCrew.close}
          onAssigned={refetch}
          eventId={event.id}
          roleId={modals.assignCrew.selectedRole.id}
          roleName={modals.assignCrew.selectedRole.name}
        />
      )}

      {/* Add Role Modal */}
      <AddRoleModal
        isOpen={modals.addRole.isOpen}
        onClose={modals.addRole.close}
        eventIds={[event.id]}
        onRoleAdded={refetch}
      />

      {/* Edit Role Modal */}
      <AddRoleModal
        isOpen={!!editingRole}
        onClose={() => setEditingRole(null)}
        eventIds={[event.id]}
        role={editingRole}
        onRoleAdded={refetch}
      />

      {/* Delete Role Confirmation */}
      <ConfirmDialog
        isOpen={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        message={
          roleToDelete
            ? `Delete "${roleToDelete.roleName}"?` +
              ((roleToDelete.assignments?.length ?? 0) > 0
                ? ` This will also remove ${roleToDelete.assignments!.length} crew assignment${
                    roleToDelete.assignments!.length !== 1 ? 's' : ''
                  } for this role.`
                : '') +
              ' This cannot be undone.'
            : ''
        }
        confirmLabel="Delete"
        isLoading={isDeletingRole}
        variant="danger"
      />

      {/* Confirm terminal status change (CANCELLED / COMPLETED) */}
      <ConfirmDialog
        isOpen={!!pendingStatus}
        onClose={() => setPendingStatus(null)}
        onConfirm={async () => {
          const target = pendingStatus;
          setPendingStatus(null);
          if (target) await applyStatusChange(target);
        }}
        title={pendingStatus === 'CANCELLED' ? 'Cancel this event?' : 'Mark event completed?'}
        message={
          pendingStatus === 'CANCELLED'
            ? `Set "${event.name}" to CANCELLED. Crew will no longer see this event on their dashboard.`
            : `Set "${event.name}" to COMPLETED. The event will move off the upcoming list.`
        }
        confirmLabel={pendingStatus === 'CANCELLED' ? 'Cancel event' : 'Mark completed'}
        cancelLabel="Keep current status"
        variant={pendingStatus === 'CANCELLED' ? 'danger' : 'default'}
        isLoading={isUpdatingStatus}
      />

      {/* Confirm event-scoped assignment notifications */}
      <ConfirmDialog
        isOpen={notifyConfirmOpen}
        onClose={() => setNotifyConfirmOpen(false)}
        onConfirm={handleSendNotifications}
        title="Send assignment notifications?"
        message={
          `This will email ${pendingAssignments.length} crew member${
            pendingAssignments.length !== 1 ? 's' : ''
          } on this event. Each person gets one email covering all of their pending assignments` +
          (otherEventPendingCount
            ? `, including ${otherEventPendingCount} assignment${
                otherEventPendingCount !== 1 ? 's' : ''
              } on other events`
            : '') +
          '. Crewing multiple events? Assign everything first, then send once.'
        }
        confirmLabel={`Send to ${pendingAssignments.length} crew member${
          pendingAssignments.length !== 1 ? 's' : ''
        }`}
        cancelLabel="Cancel"
        variant="default"
        isLoading={isSendingNotifications}
      />

      {/* Timeline Entry Modal */}
      <TimelineEntryModal
        isOpen={modals.timeline.isOpen}
        onClose={modals.timeline.close}
        eventId={event.id}
        eventDate={event.eventDate}
        entry={modals.timeline.editingEntry}
        onSaved={refetch}
      />

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={modals.document.isOpen}
        onClose={modals.document.close}
        eventId={event.id}
        onUploaded={() => invalidateDocuments(event.id)}
      />

      {/* See Availability Modal */}
      <EventAvailabilityModal
        isOpen={modals.availability.isOpen}
        onClose={modals.availability.close}
        eventId={event.id}
        eventName={event.name}
        eventStatus={event.status}
      />

      {/* Message Crew Modal */}
      <ComposeMessageModal
        isOpen={modals.message.isOpen}
        onClose={modals.message.close}
        onSuccess={() => {}}
        preselectedEventId={event.id}
        onPendingNotificationsQueued={markPendingAsQueued}
      />

      {/* Hours Entry Modal */}
      <HoursEntryModal
        isOpen={hoursModal.isOpen}
        onClose={closeHoursModal}
        onSaved={refetch}
        assignmentId={hoursModal.assignmentId}
        crewName={hoursModal.crewName}
        roleName={hoursModal.roleName}
        effectiveRate={hoursModal.effectiveRate}
        effectiveRateType={hoursModal.effectiveRateType}
        currentHours={hoursModal.hoursWorked}
        currentCheckIn={hoursModal.checkInTime}
        currentCheckOut={hoursModal.checkOutTime}
        scheduledHours={event ? scheduledHours(event) : null}
      />
    </div>
  );
}

// =============================================================================
// SortableRoleRow — wraps a <tr> with dnd-kit sortable behavior. The handle TD
// receives `attributes`, `listeners`, and `setActivatorNodeRef` so only the
// grip icon initiates a drag (clicking elsewhere on the row stays inert).
// =============================================================================
interface SortableHandleProps {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
}

function SortableRoleRow({
  id,
  children,
}: {
  id: string;
  children: (handle: SortableHandleProps) => React.ReactNode;
}) {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners, setActivatorNodeRef } =
    useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
    background: isDragging ? '#f9fafb' : undefined,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      {children({ attributes, listeners, setActivatorNodeRef })}
    </tr>
  );
}
